import { FilePdfOutlined } from '@ant-design/icons';
import { Button, DatePicker, Select, Space } from 'antd';
import Table, { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { RangeValue } from 'rc-picker/lib/interface';
import moment from 'moment';
import { FilterValue, SorterResult, SortOrder } from 'antd/lib/table/interface';
import React from 'react';
import { Link } from 'react-router-dom';
import { defaultFormat, defaultTablePagination, universalFormat } from '../../../config/constants';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { translations } from '../../../config/translations';
import { formatDate } from '../../../helpers/DateHelper';
import { downloadFile } from '../../../helpers/FileHelper';
import { showError } from '../../../helpers/NotificationHelper';
import { getTableLocale } from '../../../helpers/TableHelper';
import {
    applySearchParams,
    getFilterParamsString,
    getPagingSortingParams,
} from '../../../helpers/SearchParamsHelper';
import {
    ToolTransferVm,
    ToolTransfersClient,
    ToolVm,
    ToolsClient,
    TransferEmployeeVm,
    TransferWarehouseVm,
} from '../../../utils/api';
import { locationKey, parseLocationKey, toolLabel, toolNames } from '../helpers';
import LocationSelect from '../location-select/LocationSelect';
import TransferStatusTag from '../transfer-status-tag/TransferStatusTag';
import { Props } from './index';

const { RangePicker } = DatePicker;

/** Quick periods for the date range: the PDF is mostly asked for per year. */
const PERIOD_PRESETS = {
    [translations.transfers.thisYear]: [moment().startOf('year'), moment().endOf('year')],
    [translations.transfers.lastYear]: [
        moment().subtract(1, 'year').startOf('year'),
        moment().subtract(1, 'year').endOf('year'),
    ],
    [translations.transfers.thisMonth]: [moment().startOf('month'), moment().endOf('month')],
} as Record<string, [moment.Moment, moment.Moment]>;

/** One page holds every tool, archived ones too (they still appear on completed transfers). */
const ALL_TOOLS_PAGE_SIZE = 10000;

interface State {
    transfers?: ToolTransferVm[];
    warehouses?: TransferWarehouseVm[];
    employees?: TransferEmployeeVm[];
    tools?: ToolVm[];
    loading?: boolean;
    exporting?: boolean;
    totalItems?: number;
}

class TransferTable extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);
        this.state = {
            transfers: [],
            warehouses: [],
            employees: [],
            tools: [],
            totalItems: 0,
        };
    }

    public componentDidMount = () => {
        this.getLocations();
        this.getTools();
        this.getTransfers();
    };

    public componentDidUpdate = (prevProps: Props) => {
        const { location } = this.props;

        if (prevProps.location.search !== location.search) {
            this.getTransfers();
        }
    };

    private getLocations = async () => {
        const client = new ToolTransfersClient();
        const [warehouses, employees] = await Promise.all([
            client.getWarehouses(),
            client.getEmployees(),
        ]);

        this.setState({ warehouses, employees });
    };

    private getTools = async () => {
        const result = await new ToolsClient().getPaginated(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            1,
            ALL_TOOLS_PAGE_SIZE,
            'number',
            'ascend'
        );

        this.setState({ tools: result.pageItems });
    };

    private handleTableChange = async (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<ToolTransferVm> | SorterResult<ToolTransferVm>[] = {}
    ) => {
        const {
            history,
            location: { search },
        } = this.props;

        const currSearchParams = new URLSearchParams(search);
        const tableSearchParams = this.createTableSearchParams(pagination, filters, sorter);
        const newSearchParams = applySearchParams(currSearchParams, tableSearchParams);

        history.push({
            pathname: history.location.pathname,
            search: newSearchParams.toString(),
        });
    };

    private createTableSearchParams = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<ToolTransferVm> | SorterResult<ToolTransferVm>[] = {}
    ): URLSearchParams => {
        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const filterParamsString = getFilterParamsString(filters);
        const singleSorter = sorter as SorterResult<ToolTransferVm>;
        const tableSearchParams = new URLSearchParams(filterParamsString);
        const activeTab = params.get('activeTab') ? params.get('activeTab') : 'active';
        const warehouseId = params.get('warehouseId');
        const employeeId = params.get('employeeId');
        const toolId = params.get('toolId');

        tableSearchParams.append('pageIndex', pagination.current?.toString() || '1');
        tableSearchParams.append(
            'pageSize',
            pagination.pageSize?.toString() || pagination.defaultPageSize?.toString() || ''
        );
        tableSearchParams.append(
            'sortBy',
            singleSorter.field?.toString() && singleSorter.order
                ? singleSorter.field?.toString()
                : ''
        );
        tableSearchParams.append('sortOrder', singleSorter.order || '');
        tableSearchParams.append('activeTab', activeTab ? activeTab : 'active');

        if (warehouseId) {
            tableSearchParams.append('warehouseId', warehouseId);
        }

        if (employeeId) {
            tableSearchParams.append('employeeId', employeeId);
        }

        if (toolId) {
            tableSearchParams.append('toolId', toolId);
        }

        ['dateFrom', 'dateTo'].forEach((key) => {
            const value = params.get(key);
            if (value) tableSearchParams.append(key, value);
        });

        return tableSearchParams;
    };

    private handleLocationChange = (key?: string) => {
        const { history, location } = this.props;
        const params = new URLSearchParams(location.search);
        const { warehouseId, employeeId } = parseLocationKey(key);

        params.delete('warehouseId');
        params.delete('employeeId');

        if (warehouseId) params.set('warehouseId', warehouseId.toString());
        if (employeeId) params.set('employeeId', employeeId.toString());

        params.set('pageIndex', '1');

        history.push({
            pathname: history.location.pathname,
            search: params.toString(),
        });
    };

    private handleToolChange = (toolId?: number) => {
        const { history, location } = this.props;
        const params = new URLSearchParams(location.search);

        params.delete('toolId');

        if (toolId) params.set('toolId', toolId.toString());

        params.set('pageIndex', '1');

        history.push({
            pathname: history.location.pathname,
            search: params.toString(),
        });
    };

    /** Period on the URL as date-only "YYYY-MM-DD" (sent as text, so no time zone shifts it). */
    private handleDateRangeChange = (value: RangeValue<moment.Moment>) => {
        const { history, location } = this.props;
        const params = new URLSearchParams(location.search);

        params.delete('dateFrom');
        params.delete('dateTo');

        if (value && value[0]) params.set('dateFrom', value[0].format(universalFormat));
        if (value && value[1]) params.set('dateTo', value[1].format(universalFormat));

        params.set('pageIndex', '1');

        history.push({
            pathname: history.location.pathname,
            search: params.toString(),
        });
    };

    /** List filters from the URL, shared by the list request and the PDF. */
    private getListFilters = () => {
        const params = new URLSearchParams(this.props.location.search);
        const warehouseId = params.get('warehouseId');
        const employeeId = params.get('employeeId');
        const toolId = params.get('toolId');

        return {
            activeOnly: params.get('activeTab') !== 'completed',
            warehouseId: warehouseId && parseInt(warehouseId) ? parseInt(warehouseId) : undefined,
            employeeId: employeeId && parseInt(employeeId) ? parseInt(employeeId) : undefined,
            dateFrom: params.get('dateFrom') || undefined,
            dateTo: params.get('dateTo') || undefined,
            toolId: toolId && parseInt(toolId) ? parseInt(toolId) : undefined,
        };
    };

    /** PDF of exactly what the list shows: same tab, location, tool and period. */
    private handleExportPdf = async () => {
        const f = this.getListFilters();

        this.setState({ exporting: true });

        try {
            downloadFile(
                await new ToolTransfersClient().exportPdf(
                    f.activeOnly,
                    f.warehouseId,
                    f.employeeId,
                    f.dateFrom,
                    f.dateTo,
                    f.toolId
                )
            );
        } catch {
            showError(translations.tools.exportPdfError);
        } finally {
            this.setState({ exporting: false });
        }
    };

    /** The whole row opens the transfer; Ctrl/Cmd-click opens it in a new tab like a link. */
    private handleRowClick = (e: React.MouseEvent, record: ToolTransferVm) => {
        // The "Detalji" link navigates on its own; selecting text to copy must not navigate.
        if ((e.target as HTMLElement).closest('a') || window.getSelection()?.toString()) {
            return;
        }

        const url = Routes.ROUTE_TRANSFERS_READ.replace(
            ReplaceStrings.ENTITY_ID,
            record.id.toString()
        );

        if (e.ctrlKey || e.metaKey) {
            window.open(url, '_blank');
        } else {
            this.props.history.push(url);
        }
    };

    public getTransfers = async () => {
        this.setState({
            loading: true,
        });

        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const { pageIndexParam, pageSizeParam, sortByParam, sortOrderParam } =
            getPagingSortingParams(params);
        const f = this.getListFilters();

        const result = await new ToolTransfersClient().getPaginated(
            pageIndexParam && parseInt(pageIndexParam) ? parseInt(pageIndexParam) : 1,
            pageSizeParam && parseInt(pageSizeParam)
                ? parseInt(pageSizeParam)
                : defaultTablePagination.defaultPageSize,
            sortByParam || undefined,
            sortOrderParam || undefined,
            f.activeOnly,
            f.warehouseId,
            f.employeeId,
            f.dateFrom,
            f.dateTo,
            f.toolId
        );

        this.setState({
            transfers: result.pageItems,
            loading: false,
            totalItems: result.totalItems,
        });
    };

    public render(): React.ReactElement {
        const { transfers, warehouses, employees, tools, loading, exporting, totalItems } =
            this.state;
        const { location } = this.props;
        const params = new URLSearchParams(location.search);
        const { sortByParam, sortOrderParam, pageIndexParam, pageSizeParam } =
            getPagingSortingParams(params);
        const warehouseId = params.get('warehouseId');
        const employeeId = params.get('employeeId');
        const toolId = params.get('toolId');
        const pagination: TablePaginationConfig = {
            ...defaultTablePagination,
            current:
                pageIndexParam && parseInt(pageIndexParam)
                    ? parseInt(pageIndexParam)
                    : defaultTablePagination.defaultCurrent,
            pageSize:
                pageSizeParam && parseInt(pageSizeParam)
                    ? parseInt(pageSizeParam)
                    : defaultTablePagination.defaultPageSize,
            total: totalItems,
        };

        const columns: ColumnsType<ToolTransferVm> = [
            {
                title: translations.transfers.fromWarehouse,
                dataIndex: 'fromName',
            },
            {
                title: translations.transfers.toWarehouse,
                dataIndex: 'toName',
            },
            {
                title: translations.transfers.createdBy,
                dataIndex: 'createdByName',
            },
            {
                title: translations.transfers.createdDate,
                dataIndex: 'createdDate',
                sorter: true,
                sortOrder: (sortByParam === 'createdDate' ? sortOrderParam : null) as SortOrder,
                render: (value: Date): string => formatDate(value),
            },
            {
                title: translations.transfers.status,
                dataIndex: 'status',
                render: (_: unknown, record: ToolTransferVm): React.ReactElement => (
                    <TransferStatusTag status={record.status} />
                ),
            },
            {
                title: translations.transfers.tools,
                dataIndex: 'items',
                render: (_: unknown, record: ToolTransferVm): string => toolNames(record),
            },
            {
                title: translations.transfers.shippedBy,
                dataIndex: 'shippedByName',
                render: (_: unknown, record: ToolTransferVm): string =>
                    record.shippedByName
                        ? record.shippedByName + ' (' + formatDate(record.shippedDate) + ')'
                        : '',
            },
            {
                title: translations.transfers.receivedBy,
                dataIndex: 'receivedByName',
                render: (_: unknown, record: ToolTransferVm): string =>
                    record.receivedByName
                        ? record.receivedByName + ' (' + formatDate(record.receivedDate) + ')'
                        : '',
            },
            {
                title: translations.general.actions,
                key: 'actions',
                width: 100,
                align: 'left',
                className: 'no-print',
                render: (value: ToolTransferVm): React.ReactElement => (
                    <Link
                        to={Routes.ROUTE_TRANSFERS_READ.replace(
                            ReplaceStrings.ENTITY_ID,
                            value.id.toString()
                        )}
                    >
                        {translations.general.details}
                    </Link>
                ),
            },
        ];

        return (
            <>
                <Space style={{ marginBottom: 16 }} wrap className="no-print">
                    <LocationSelect
                        allowClear
                        warehouses={warehouses || []}
                        employees={employees || []}
                        style={{ width: 260 }}
                        placeholder={translations.transfers.location}
                        value={locationKey(
                            warehouseId ? parseInt(warehouseId) : undefined,
                            employeeId ? parseInt(employeeId) : undefined
                        )}
                        onChange={this.handleLocationChange}
                    />

                    <Select
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        style={{ width: 300 }}
                        placeholder={translations.transfers.tool}
                        value={toolId ? parseInt(toolId) : undefined}
                        onChange={this.handleToolChange}
                    >
                        {(tools || []).map((tool: ToolVm) => (
                            <Select.Option key={tool.id} value={tool.id}>
                                {toolLabel(tool)}
                            </Select.Option>
                        ))}
                    </Select>

                    <RangePicker
                        allowEmpty={[true, true]}
                        format={defaultFormat}
                        ranges={PERIOD_PRESETS}
                        placeholder={[translations.general.dateFrom, translations.general.dateTo]}
                        value={[
                            params.get('dateFrom')
                                ? moment(params.get('dateFrom'), universalFormat)
                                : null,
                            params.get('dateTo')
                                ? moment(params.get('dateTo'), universalFormat)
                                : null,
                        ]}
                        onChange={this.handleDateRangeChange}
                    />

                    <Button
                        icon={<FilePdfOutlined />}
                        loading={exporting}
                        onClick={this.handleExportPdf}
                    >
                        {translations.tools.exportPdf}
                    </Button>
                </Space>

                <Table
                    locale={getTableLocale()}
                    columns={columns}
                    dataSource={transfers}
                    loading={loading}
                    rowKey={(record: ToolTransferVm): string => record.id?.toString()}
                    onChange={this.handleTableChange}
                    onRow={(record: ToolTransferVm) => ({
                        style: { cursor: 'pointer' },
                        onClick: (e: React.MouseEvent) => this.handleRowClick(e, record),
                    })}
                    pagination={pagination}
                    bordered
                    size="small"
                />
            </>
        );
    }
}

export default TransferTable;
